/*
 * Checkout Free Sample Notification - FINAL VERSION
 * Clean, debounced, no duplicates
 */

(function() {
  'use strict';
  
  // ========== CONFIGURATION ==========
  const CONFIG = {
    SAMPLES_HANDLE: '5ml-order-samples-create-your-own-discovery-set',
    SAMPLES_PAGE_URL: '/products/order-samples',
    
    MESSAGES: {
      1: {
        title: '🎁 You unlocked 1 FREE 5ml sample',
        description: 'Choose your favourite fragrance from the Order Samples page.<br>Write the sample code in Order Notes.',
        note: 'Eligible for prepaid orders only. These complimentary samples are separate from any samples already in your cart.',
        showLink: true,
        placeholder: 'Sample: 003 IMGNTN'
      },
      2: {
        title: '🎁 You unlocked 2 FREE 5ml samples',
        description: 'Choose your favourite fragrances from the Order Samples page.<br>Write the sample codes in Order Notes.',
        note: 'Eligible for prepaid orders only. These complimentary samples are separate from any samples already in your cart.',
        showLink: true,
        placeholder: 'Samples: 003 IMGNTN, 009 PM'
      }
    }
  };
  
  // ========== CHECK ELIGIBILITY ==========
  async function checkEligibility() {
    try {
      // First, force a cart update to get fresh data
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      }).catch(() => {}); // Ignore errors, just trying to refresh
      
      // Small delay to let server process
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Now fetch the fresh cart data with cache buster
      const timestamp = new Date().getTime();
      const response = await fetch(`/cart.js?t=${timestamp}`, {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache' }
      });
      const cart = await response.json();
      // Small delay to let server process
      await new Promise(resolve => setTimeout(resolve, 200));
      // Filter out sample products from count
      const nonSampleItems = cart.items.filter(item => 
        item.handle !== CONFIG.SAMPLES_HANDLE
      );
      
      // Count total quantity of bottles (excluding samples)
      const totalBottles = nonSampleItems.reduce((sum, item) => sum + item.quantity, 0);
      
      // Return null if no bottles (including when cart is empty or all items removed)
      if (totalBottles === 0) {
        return null;
      } else if (totalBottles === 1) {
        return 1;  // Phase 1: 1 free sample
      } else if (totalBottles >= 2) {
        return 2;  // Phase 2: 2 free samples (max)
      }
      
      return null;
      
    } catch (error) {
      console.error('Free Sample Notification - Error checking eligibility:', error);
      return null;
    }
  }
  
  // ========== FIND INJECTION POINT ==========
  function findInjectionPoint() {
    let selectors;
    
    if (window.location.pathname.includes('/cart')) {
      // Cart page selectors
      selectors = [
        'main-cart .cart__footer',
        '.cart__footer',
        '.cart-footer',
        'main-cart',
        '[data-cart-footer]',
        '.cart__ctas',
        '.cart-drawer__footer',
        '.drawer__footer'
      ];
    } else {
      // Cart drawer selectors
      selectors = [
        '.cart-drawer__footer',
        '.drawer__footer',
        '.cart__footer',
        '.cart-footer',
        '[data-cart-footer]',
        'main-cart'
      ];
    }
    
    for (const selector of selectors) {
      const el = document.querySelector(selector);
      if (el && el.offsetParent !== null) {
        return el;
      }
    }
    
    return null;
  }
  
  // ========== RENDER NOTIFICATION ==========
  async function renderNotification() {
    try {
      // Only run on cart/checkout pages or when drawer is open
      const isCheckout = window.location.pathname.includes('/checkout') || 
                         window.location.pathname.includes('/cart');
      
      const drawer = document.querySelector('cart-drawer');
      const isDrawerOpen = drawer && drawer.classList.contains('active');
      
      if (!isCheckout && !isDrawerOpen) {
        return;
      }
      
      // Remove ALL existing notifications (prevent duplicates)
      const existingNotifications = document.querySelectorAll('.free-sample-notification');
      existingNotifications.forEach(notif => notif.remove());
      
      // Check eligibility (returns 1, 2, or null)
      const bottleCount = await checkEligibility();
      
      if (!bottleCount) {
        // Reset placeholder to default when no notification
        resetPlaceholder();
        return;
      }
      
      // Get message for this bottle count
      const message = CONFIG.MESSAGES[bottleCount];
      if (!message) {
        return;
      }
      
      // Find injection point
      const target = findInjectionPoint();
      if (!target) {
        return;
      }
      
      // Find the cart__blocks element (checkout section)
      const cartBlocks = target.querySelector('.cart__blocks');
      
      // Build notification HTML (no emoji icon)
      let notificationHTML = `
        <div id="free-sample-notification" class="free-sample-notification">
          <div class="notification-content">
            <h3 class="notification-title">${message.title}</h3>
            <p class="notification-description">${message.description}</p>
            <p class="notification-note">${message.note}</p>
      `;
      
      if (message.showLink) {
        notificationHTML += `
            <a href="${CONFIG.SAMPLES_PAGE_URL}" class="notification-link" target="_blank" rel="noopener">
              Choose Your Sample →
            </a>
      `;
      }
      
      notificationHTML += `
          </div>
        </div>
      `;
      
      // Insert notification
      if (cartBlocks) {
        cartBlocks.insertAdjacentHTML('beforebegin', notificationHTML);
      } else {
        target.insertAdjacentHTML('afterbegin', notificationHTML);
      }
      
      // Update placeholder text
      updatePlaceholder(message);
      
      injectStyles();
      
    } catch (error) {
      console.error('Free Sample Notification - Error rendering:', error);
    }
  }
  
  // ========== UPDATE PLACEHOLDER ==========
  function updatePlaceholder(message) {
    try {
      // Find ALL note fields (cart page + cart drawer)
      const noteFields = [
        document.querySelector('#Cart-note'),
        document.querySelector('#CartDrawer-Note'),
        document.querySelector('[name="note"]')
      ].filter(Boolean);
      
      if (noteFields.length === 0) {
        return;
      }
      
      // Update all found textareas
      noteFields.forEach(noteField => {
        noteField.placeholder = message.placeholder;
        noteField.setAttribute('placeholder', message.placeholder);
      });
      
    } catch (error) {
      console.error('Free Sample Notification - Error updating placeholder:', error);
    }
  }
  
  // ========== RESET PLACEHOLDER ==========
  function resetPlaceholder() {
    try {
      const noteFields = [
        document.querySelector('#Cart-note'),
        document.querySelector('#CartDrawer-Note'),
        document.querySelector('[name="note"]')
      ].filter(Boolean);
      
      noteFields.forEach(noteField => {
        noteField.placeholder = 'Order special instructions';
        noteField.setAttribute('placeholder', 'Order special instructions');
      });
      
    } catch (error) {
      // Silent fail
    }
  }
  
  // ========== STYLES ==========
  function injectStyles() {
    if (document.getElementById('free-sample-notification-styles')) return;
    
    const styles = `
      <style id="free-sample-notification-styles">
        .free-sample-notification {
          background: linear-gradient(135deg, #fff9e6 0%, #fff 100%);
          border: 2px solid #ffc107;
          border-radius: 12px;
          padding: 20px;
          margin: 20px 0;
          display: flex;
          gap: 16px;
          align-items: flex-start;
          box-shadow: 0 4px 12px rgba(255, 193, 7, 0.1);
          animation: slideIn 0.5s ease-out;
        }
        
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
        
        .notification-content {
          flex: 1;
        }
        
        .notification-title {
          margin: 0 0 8px;
          font-size: 18px;
          font-weight: 700;
          color: #000;
          line-height: 1.3;
        }
        
        .notification-description {
          margin: 0 0 8px;
          font-size: 14px;
          color: #333;
          line-height: 1.5;
        }
        
        .notification-note {
          margin: 0 0 12px;
          font-size: 12px;
          color: #666;
          font-style: italic;
        }
        
        .notification-link {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 10px 20px;
          background: #000;
          color: #fff;
          text-decoration: none;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 600;
          transition: all 0.2s;
        }
        
        .notification-link:hover {
          background: #333;
          transform: translateX(2px);
        }
        
        /* Force placeholder to be visible */
        textarea[name="note"]::placeholder,
        #Cart-note::placeholder,
        #CartDrawer-Note::placeholder {
          color: #6b7280 !important;
          opacity: 1 !important;
          font-size: 14px !important;
        }
        
        @media (max-width: 768px) {
          .free-sample-notification {
            flex-direction: column;
            padding: 16px;
          }
          
          .notification-title {
            font-size: 16px;
          }
          
          .notification-link {
            width: 100%;
            justify-content: center;
          }
        }
      </style>
    `;
    
    document.head.insertAdjacentHTML('beforeend', styles);
  }
  
  // ========== DEBOUNCE MECHANISM ==========
  let renderTimeout = null;
  
  function debouncedRender() {
    if (renderTimeout) {
      clearTimeout(renderTimeout);
    }
    renderTimeout = setTimeout(() => {
      renderNotification();
      renderTimeout = null;
    }, 800);  // 800ms debounce - gives cart.js time to update
  }
  
  // ========== INITIALIZE ==========
  function init() {
    try {
      // Run on page load
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          setTimeout(renderNotification, 1500);
        });
      } else {
        setTimeout(renderNotification, 1500);
      }
      
      // Listen for cart updates (Dawn theme events)
      document.addEventListener('cart:updated', () => {
        debouncedRender();
      });
      
      document.addEventListener('cart:refresh', () => {
        debouncedRender();
      });
      
      // Listen for quantity input changes
      document.addEventListener('change', (e) => {
        if (e.target.matches('input[name="updates[]"]') || 
            e.target.matches('.quantity__input') ||
            e.target.matches('input[type="number"]')) {
          debouncedRender();
        }
      });
      
      // Listen for +/- button clicks and removals
      document.addEventListener('click', (e) => {
        if (e.target.closest('.quantity__button') ||
            e.target.closest('[name="plus"]') ||
            e.target.closest('[name="minus"]') ||
            e.target.closest('[name="update"]') || 
            e.target.closest('.cart-item__remove') ||
            e.target.closest('[data-index]')) {
          debouncedRender();
        }
      });
      
      // Watch for cart drawer opening
      const drawerObserver = new MutationObserver(() => {
        const drawer = document.querySelector('cart-drawer');
        if (drawer && drawer.classList.contains('active')) {
          debouncedRender();
        }
      });
      
      const drawer = document.querySelector('cart-drawer');
      if (drawer) {
        drawerObserver.observe(drawer, { 
          attributes: true, 
          attributeFilter: ['class'] 
        });
      }
      
    } catch (error) {
      console.error('Free Sample Notification - Initialization error:', error);
    }
  }
  
  init();
  
})();